import { useCallback } from 'react'

import {
  type InfiniteData,
  useConvenientInfiniteQuery,
  useQuery,
} from '@fv/client-core'
import type { MessageFilterOpts, UnreadCountResponse } from '@fv/models'
import { removeNilOrEmpty } from '@fv/models/core'

import { useCurrentUser } from '@/hooks/settings/useAppContext'
import { shipperFetch } from '@/utils/shipperFetch'

import { useAccountUsers } from '../account-users/queries'
import { canAccessMessages } from './hooks'
import type { LoadCarrierMessage, Message, MessageInboxItem } from './types'

export const messageQueryKeys = {
  all: ['messages'],
  unreadCount: () => [...messageQueryKeys.all, 'unread-count'],
  inboxRoot: () => [...messageQueryKeys.all, 'inbox'],
  inbox: (filter: MessageFilterOpts) => [
    ...messageQueryKeys.inboxRoot(),
    filter,
  ],
  load: (id?: string) => [...messageQueryKeys.all, 'load', id ?? null] as const,
}
export const useUnreadMessageCount = () => {
  const enabled = canAccessMessages()
  return useQuery({
    queryKey: messageQueryKeys.unreadCount(),
    queryFn: async () => {
      return await shipperFetch<UnreadCountResponse>('/messages/unread-count')
    },
    enabled,
  })
}

const MESSAGE_LIMIT = 50
type MessageWithContinuationToken = MessageInboxItem & {
  continuationToken?: string
}
export const useMessageInbox = (filter: MessageFilterOpts) => {
  return useConvenientInfiniteQuery({
    queryKey: messageQueryKeys.inbox(filter),
    queryFn: async ({ pageParam }) =>
      shipperFetch<MessageWithContinuationToken[]>('/messages', {
        query: removeNilOrEmpty({
          ...filter,
          limit: MESSAGE_LIMIT,
          continuationToken: pageParam,
        }),
      }),
    limit: MESSAGE_LIMIT,
  })
}

export type MessageInboxQueryData = InfiniteData<{
  data: MessageInboxItem[]
  continuationToken?: string
}>

const fetchMessages = async (loadId?: string): Promise<Message[]> => {
  if (!loadId) throw new Error('`loadId` required to fetch messages')
  return shipperFetch<Message[]>(`/shipments/${loadId}/messages`)
}
interface UseLoadMessages {
  carrierId?: string
  userId?: string
  loadId: string
  enabled?: boolean
}
export const useLoadMessages = (opts: UseLoadMessages) => {
  const user = useCurrentUser()
  const hasAccess = canAccessMessages()

  const { carrierId, userId, loadId, enabled } = opts

  const select = useCallback(
    (data: Message[]): LoadCarrierMessage[] => {
      return data
        .filter(m =>
          m.participants.some(
            p => p.carrierId === carrierId && p.role === 'carrier',
          ),
        )
        .map(m => ({
          ...m,
          author: m.participants.find(p => p.isAuthor)!,
          currentShipperRecipient: m.participants.find(p => p.id === user._id),
          carrierRecipients: m.participants.filter(
            p => !p.isAuthor && (p.carrierId === carrierId || p.id === userId),
          ),
        }))
    },
    [carrierId, userId, user],
  )

  return useQuery({
    queryKey: messageQueryKeys.load(loadId),
    queryFn: () => fetchMessages(loadId),
    enabled: Boolean(loadId) && hasAccess && enabled,
    select,
  })
}

export const useHasUnreadMessagesOnLoad = (opts: UseLoadMessages) => {
  const loadMessages = useLoadMessages(opts)
  const user = useCurrentUser()
  return loadMessages.data?.some(m =>
    m.participants.some(p => p.id === user._id && !p.isAuthor && !p.readDate),
  )
}

export const useMentionableUsers = () => {
  const user = useCurrentUser()
  const usersQuery = useAccountUsers()
  return (
    usersQuery.data?.filter(
      u =>
        u._id !== user._id && u.access?.permissions?.includes('load-creator'),
    ) ?? []
  )
}
